import { useState, useMemo } from 'react';
import { Metric } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Search, Filter, X } from 'lucide-react';
import { includeEndpointInDashboardMetrics } from '../utils/dashboardFilters';

interface EndpointMetricsProps {
  endpoints: Metric[];
}

export default function EndpointMetrics({ endpoints }: EndpointMetricsProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState<string>('');

  const filteredMetricsEndpoints = useMemo(() => {
    return endpoints.filter((endpoint) => includeEndpointInDashboardMetrics(endpoint.endpoint));
  }, [endpoints]);

  // Get unique methods from filtered endpoints
  const uniqueMethods = useMemo(() => {
    const methods = new Set(filteredMetricsEndpoints.map(e => e.method));
    return Array.from(methods).sort();
  }, [filteredMetricsEndpoints]);

  // Filter endpoints based on search term and method
  const filteredEndpoints = useMemo(() => {
    return filteredMetricsEndpoints.filter(endpoint => {
      const matchesSearch = searchTerm === '' || 
        endpoint.endpoint.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMethod = methodFilter === '' || endpoint.method === methodFilter;
      return matchesSearch && matchesMethod;
    });
  }, [filteredMetricsEndpoints, searchTerm, methodFilter]);

  const chartData = filteredEndpoints.slice(0, 10).map((endpoint) => ({
    name: endpoint.endpoint.length > 30 
      ? endpoint.endpoint.substring(0, 30) + '...' 
      : endpoint.endpoint,
    fullName: endpoint.endpoint,
    method: endpoint.method,
    requests: endpoint.count,
    avgTime: Math.round(endpoint.avgResponseTime),
    errors: endpoint.errorCount,
    successes: endpoint.successCount,
  }));

  const clearFilters = () => {
    setSearchTerm('');
    setMethodFilter('');
  };

  const hasActiveFilters = searchTerm !== '' || methodFilter !== '';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Endpoints Mais Utilizados
          {hasActiveFilters && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredEndpoints.length} de {filteredMetricsEndpoints.length})
            </span>
          )}
        </h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
            Limpar filtros
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="mb-6 flex flex-wrap gap-4">
        {/* Campo de busca por endpoint */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar endpoint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Filtro por método HTTP */}
        <div className="min-w-[150px]">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
            >
              <option value="">Todos os métodos</option>
              {uniqueMethods.map(method => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {filteredEndpoints.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium mb-2">Nenhum endpoint encontrado</p>
          <p className="text-sm">Tente ajustar os filtros de busca</p>
        </div>
      ) : (
        <>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            angle={-45}
            textAnchor="end"
            height={100}
          />
          <YAxis />
          <Tooltip 
            formatter={(value: any, name: string) => {
              if (name === 'avgTime') return `${value}ms`;
              return value;
            }}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                return `${payload[0].payload.method} ${payload[0].payload.fullName}`;
              }
              return label;
            }}
          />
          <Legend />
          <Bar dataKey="requests" fill="#008b95" name="Requisições" />
          <Bar dataKey="errors" fill="#ef4444" name="Erros" />
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Método
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Endpoint
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Requisições
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Tempo Médio
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Erros
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEndpoints.slice(0, 10).map((endpoint, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-primary-100 text-primary-800">
                    {endpoint.method}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{endpoint.endpoint}</td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                  {endpoint.count}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                  {Math.round(endpoint.avgResponseTime)}ms
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                  {endpoint.errorCount > 0 ? (
                    <span className="text-red-600 font-semibold">{endpoint.errorCount}</span>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
}

